"use client";

import {
  Crosshair,
  ImagePlus,
  MousePointer2,
  RotateCcw,
  ScanSearch,
  X,
} from "lucide-react";
import { FormEvent, useRef, useState } from "react";
import { ReasoningStyleSelector } from "@/components/chat/ReasoningStyleSelector";
import { Sam3Canvas } from "./Sam3Canvas";
import { useSam3Selection } from "./useSam3Selection";
import { useObjectRiskAssessmentRun } from "./useObjectRiskAssessmentRun";
import { ObjectRiskAssessmentRunPanel } from "./ObjectRiskAssessmentRunPanel";

const humanBytes = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.ceil(bytes / 1024)} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export function ObjectRiskAssessmentWorkspace() {
  const inputRef = useRef<HTMLInputElement>(null);
  const controller = useSam3Selection();
  const run = useObjectRiskAssessmentRun();
  const [textQuery, setTextQuery] = useState("");
  const [focusLabel, setFocusLabel] = useState("");
  const [modelMode, setModelMode] = useState<"fast" | "pro">("fast");
  const [showBoundingBox, setShowBoundingBox] = useState(false);
  const isPreparing = controller.status === "preparing";
  const hasImage = Boolean(controller.image);
  const showSelection =
    run.state === "idle" || run.state === "failed" || run.state === "cancelled";
  const roiResult =
    controller.result &&
    "mode" in controller.result &&
    controller.result.mode === "roi"
      ? controller.result
      : null;
  const textResult =
    controller.result && "prompt" in controller.result
      ? controller.result
      : null;

  const submitTextSearch = (event: FormEvent) => {
    event.preventDefault();
    const prompt = textQuery.trim();
    if (!prompt) return;
    setFocusLabel(prompt);
    void controller.runText(prompt);
  };

  const openPicker = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  return (
    <section
      data-object-risk-assessment-workspace
      data-seexr-notice-landing={!hasImage && !isPreparing && run.state === "idle" ? "true" : undefined}
      className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-background px-4 pb-10 pt-20 md:px-8 md:pt-10"
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void controller.selectFile(file);
        }}
      />

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
        <header className="space-y-2">
          <div className="flex items-center gap-2 text-seexr-primary">
            <Crosshair size={18} strokeWidth={2.2} aria-hidden />
            <span className="text-xs font-bold uppercase tracking-[0.16em]">
              Object-focused safety
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 dark:text-gray-50 md:text-3xl">
            객체를 선택하고 그 대상에 집중해 위험성을 평가합니다
          </h1>
          <p className="max-w-3xl text-sm leading-6 text-gray-500 dark:text-gray-400">
            포인트로 정확한 대상을 짚거나 라쏘로 검색 영역을 둘러 SAM3가 실제 객체 경계를
            찾게 할 수 있습니다.
          </p>
        </header>

        {showSelection && (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="overflow-hidden rounded-[28px] border border-gray-200 bg-white/60 dark:border-gray-800 dark:bg-white/[0.025]">
            {!hasImage ? (
              <button
                type="button"
                onClick={openPicker}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  const file = event.dataTransfer.files?.[0];
                  if (file) void controller.selectFile(file);
                }}
                className="group flex min-h-[480px] w-full cursor-pointer flex-col items-center justify-center px-8 text-center transition hover:bg-white dark:hover:bg-white/[0.04]"
              >
                <span className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 transition group-hover:text-seexr-primary dark:bg-white/[0.06] dark:text-gray-400">
                  <ImagePlus size={26} strokeWidth={1.8} aria-hidden />
                </span>
                <strong className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  현장 사진을 올려주세요
                </strong>
                <span className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  클릭하거나 드래그해서 추가 · PNG · JPG · WEBP · 최대 10MB
                </span>
              </button>
            ) : (
              <>
                <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-800">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {controller.image?.file.name}
                    </p>
                    <p className="mt-0.5 text-xs text-gray-400">
                      {controller.image?.width}×{controller.image?.height} ·{" "}
                      {controller.image ? humanBytes(controller.image.file.size) : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openPicker}
                    className="rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.04]"
                  >
                    다른 이미지
                  </button>
                </div>

                {isPreparing && (
                  <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
                    <div className="mb-2 flex items-center justify-between text-xs">
                      <span className="font-semibold text-gray-600 dark:text-gray-300">
                        SAM3 이미지 특징 준비 중
                      </span>
                      <span className="text-gray-400">1 / 1</span>
                    </div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.07]">
                      <div className="h-full w-2/3 animate-pulse rounded-full bg-seexr-primary" />
                    </div>
                  </div>
                )}

                <div className="relative flex min-h-[440px] items-center justify-center overflow-hidden bg-gray-50 dark:bg-black/20">
                  {controller.image && (
                    <Sam3Canvas
                      imageUrl={controller.image.url}
                      width={controller.image.width}
                      height={controller.image.height}
                      mode={controller.mode}
                      points={controller.points}
                      lassoPolygon={controller.lassoPolygon}
                      lassoResetKey={controller.lassoResetKey}
                      activeInstance={controller.activeInstance}
                      pointLabel={controller.pointLabel}
                      disabled={controller.status === "preparing"}
                      showBoundingBox={showBoundingBox}
                      onPoint={controller.addPoint}
                      onLassoComplete={controller.completeLasso}
                    />
                  )}
                  {controller.status === "segmenting" && (
                    <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full border border-gray-200 bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-600 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-[#1d1d1f]/90 dark:text-gray-300">
                      {controller.mode === "lasso"
                        ? "영역 안 객체 탐색 중…"
                        : controller.mode === "text"
                          ? "텍스트로 객체 찾는 중…"
                          : "선택 경계 업데이트 중…"}
                    </div>
                  )}
                </div>

                {controller.error && (
                  <div className="border-t border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300">
                    {controller.error}
                  </div>
                )}
              </>
            )}
          </div>

          <aside className="flex min-h-[480px] flex-col gap-3 rounded-[28px] border border-gray-200 bg-white/70 p-4 dark:border-gray-800 dark:bg-white/[0.025]">
            <div className="px-2 pb-1 pt-1">
              <p className="text-xs font-bold uppercase tracking-[0.14em] text-gray-400">
                선택 방법
              </p>
            </div>

            <div className="grid grid-cols-3 gap-1 rounded-2xl border border-gray-200 bg-gray-50/80 p-1 dark:border-gray-800 dark:bg-white/[0.025]">
              {([
                ["point", "포인트", MousePointer2],
                ["lasso", "라쏘", Crosshair],
                ["text", "텍스트", ScanSearch],
              ] as const).map(([value, label, Icon]) => {
                const active = controller.mode === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => controller.setMode(value)}
                    className={`flex min-w-0 items-center justify-center gap-1.5 rounded-xl px-2 py-2.5 text-xs font-semibold transition ${
                      active
                        ? "bg-white text-seexr-primary shadow-sm ring-1 ring-gray-200 dark:bg-white/[0.08] dark:ring-white/[0.08]"
                        : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    }`}
                  >
                    <Icon size={15} strokeWidth={2} />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>

            <div className="rounded-xl bg-gray-50 px-3 py-2.5 text-[11px] leading-5 text-gray-500 dark:bg-white/[0.035] dark:text-gray-400">
              {controller.mode === "point"
                ? "객체 위를 클릭합니다. 경계가 어긋나면 제외 포인트로 다듬을 수 있습니다."
                : controller.mode === "lasso"
                  ? "객체가 들어오도록 자유롭게 둘러주세요. 이미지 밖으로 나간 선은 경계에 맞춰 자동 보정됩니다."
                  : "객체 이름을 입력해 일치하는 인스턴스를 찾습니다."}
            </div>

            {hasImage && controller.mode === "text" && (
              <form onSubmit={submitTextSearch} className="space-y-2">
                <label
                  htmlFor="sam3-text-prompt"
                  className="block px-1 text-xs font-semibold text-gray-600 dark:text-gray-300"
                >
                  찾고 싶은 객체
                </label>
                <div className="flex gap-2">
                  <input
                    id="sam3-text-prompt"
                    value={textQuery}
                    onChange={(event) => setTextQuery(event.target.value)}
                    maxLength={500}
                    placeholder="예: excavator, scaffold, worker"
                    className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-seexr-primary dark:border-gray-700 dark:bg-white/[0.04] dark:text-gray-100"
                  />
                  <button
                    type="submit"
                    disabled={!textQuery.trim()}
                    className="shrink-0 rounded-xl bg-seexr-primary px-3.5 py-2.5 text-xs font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    찾기
                  </button>
                </div>
                <p className="px-1 text-[11px] leading-5 text-gray-400">
                  SAM3 텍스트 프롬프트는 현재 영어 표현이 가장 안정적입니다.
                </p>
              </form>
            )}

            {hasImage && controller.mode === "point" && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => controller.setPointLabel(1)}
                  className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${
                    controller.pointLabel === 1
                      ? "border-seexr-primary bg-seexr-primary/[0.08] text-seexr-primary"
                      : "border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/[0.04]"
                  }`}
                >
                  ＋ 대상
                </button>
                <button
                  type="button"
                  onClick={() => controller.setPointLabel(0)}
                  className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${
                    controller.pointLabel === 0
                      ? "border-red-300 bg-red-50 text-red-600 dark:border-red-900 dark:bg-red-950/20 dark:text-red-300"
                      : "border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/[0.04]"
                  }`}
                >
                  − 제외
                </button>
              </div>
            )}

            {hasImage && controller.mode !== "text" && (
              <div className="flex gap-2">
                {controller.mode === "point" && (
                  <button
                    type="button"
                    disabled={controller.points.length === 0}
                    onClick={controller.undoPoint}
                    className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-500 transition enabled:hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-35 dark:border-gray-700 dark:enabled:hover:bg-white/[0.04]"
                  >
                    <RotateCcw size={14} />
                    취소
                  </button>
                )}
                <button
                  type="button"
                  disabled={
                    controller.mode === "point"
                      ? controller.points.length === 0
                      : controller.lassoPolygon.length === 0
                  }
                  onClick={controller.clearSelection}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-500 transition enabled:hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-35 dark:border-gray-700 dark:enabled:hover:bg-white/[0.04]"
                >
                  <X size={14} />
                  지우기
                </button>
              </div>
            )}

            {controller.activeInstance && (
              <label className="flex cursor-pointer items-center justify-between rounded-xl border border-gray-200 px-3 py-2.5 dark:border-gray-800">
                <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                  표시 옵션
                </span>
                <span className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={showBoundingBox}
                    onChange={(event) => setShowBoundingBox(event.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                    style={{ accentColor: "var(--seexr-primary)" }}
                  />
                  BBox 표시
                </span>
              </label>
            )}

            {controller.result && controller.result.instances.length > 1 && (
              <div className="space-y-2 rounded-2xl border border-gray-200 p-3 dark:border-gray-800">
                <p className="px-1 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
                  객체 후보 {controller.result.instances.length}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {controller.result.instances.slice(0, 8).map((instance, index) => (
                    <button
                      key={`${instance.score}-${index}`}
                      type="button"
                      onClick={() => controller.setActiveInstanceIndex(index)}
                      className={`rounded-xl border px-2 py-2 text-left text-xs transition ${
                        controller.activeInstanceIndex === index
                          ? "border-seexr-primary bg-seexr-primary/[0.06]"
                          : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/[0.04]"
                      }`}
                    >
                      <span className="block font-semibold text-gray-700 dark:text-gray-200">
                        후보 {index + 1}
                      </span>
                      <span className="text-gray-400">
                        {(instance.score * 100).toFixed(1)}%
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {controller.activeInstance && !controller.focusDraft && (
              <div className="space-y-2 rounded-2xl border border-gray-200 p-3 dark:border-gray-800">
                <label
                  htmlFor="focus-object-label"
                  className="block px-1 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400"
                >
                  분석 대상 확정
                </label>
                <input
                  id="focus-object-label"
                  value={focusLabel}
                  onChange={(event) => setFocusLabel(event.target.value)}
                  maxLength={100}
                  placeholder="대상명 (선택 사항)"
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-seexr-primary dark:border-gray-700 dark:bg-white/[0.04] dark:text-gray-100"
                />
                <button
                  type="button"
                  onClick={() => void controller.confirmFocus(focusLabel)}
                  disabled={controller.focusPreparing}
                  className="w-full rounded-xl bg-seexr-primary px-3 py-2.5 text-xs font-bold text-white transition hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
                >
                  {controller.focusPreparing ? "객체 이미지 생성 중…" : "이 객체를 분석 대상으로 선택"}
                </button>
              </div>
            )}

            {controller.focusDraft && (
              <div className="overflow-hidden rounded-2xl border border-seexr-primary/25 bg-seexr-primary/[0.04]">
                <div className="flex gap-3 p-3">
                  <div className="flex h-20 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[linear-gradient(45deg,#f2f2f2_25%,transparent_25%,transparent_75%,#f2f2f2_75%),linear-gradient(45deg,#f2f2f2_25%,transparent_25%,transparent_75%,#f2f2f2_75%)] bg-[length:12px_12px] bg-[position:0_0,6px_6px] dark:bg-black/20">
                    {/* Browser-generated transparent SAM mask preview. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={controller.focusDraft.previewUrl}
                      alt="확정된 분석 객체"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-seexr-primary">
                      분석 대상
                    </p>
                    <p className="mt-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {controller.focusDraft.label}
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {controller.focusDraft.selectionMethod} · {(controller.focusDraft.score * 100).toFixed(1)}%
                    </p>
                  </div>
                </div>
                <div className="space-y-3 border-t border-seexr-primary/10 p-3">
                  <p className="text-[11px] leading-5 text-gray-500 dark:text-gray-400">
                    SAM mask를 적용한 투명 PNG와 원본 현장 사진을 함께 사용합니다.
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {([
                      ["fast", "빠른 답변"],
                      ["pro", "전문가 모드"],
                    ] as const).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setModelMode(value)}
                        className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${
                          modelMode === value
                            ? "border-seexr-primary bg-seexr-primary/[0.08] text-seexr-primary"
                            : "border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/[0.04]"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white/70 dark:border-gray-700 dark:bg-white/[0.035]">
                    <ReasoningStyleSelector />
                  </div>
                  <button
                    type="button"
                    disabled={!controller.image}
                    onClick={() => {
                      if (!controller.image || !controller.focusDraft) return;
                      void run.start({
                        originalFile: controller.image.file,
                        focusDraft: controller.focusDraft,
                        modelMode,
                      });
                    }}
                    className="w-full rounded-xl bg-seexr-primary px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    객체 중심 위험성평가 시작
                  </button>
                </div>
              </div>
            )}

            <div className="mt-auto space-y-2 rounded-2xl bg-gray-50 px-4 py-3 text-xs leading-5 text-gray-500 dark:bg-white/[0.04] dark:text-gray-400">
              {controller.status === "idle" && <p>이미지를 올리면 SAM3 선택 세션을 준비합니다.</p>}
              {controller.status === "preparing" && <p>사진 특징을 계산하고 있습니다.</p>}
              {(controller.status === "ready" || controller.status === "segmenting") && (
                <p>
                  SAM3 준비됨
                  {controller.embeddingSeconds !== null
                    ? ` · embedding ${controller.embeddingSeconds.toFixed(2)}초`
                    : ""}
                </p>
              )}
              {controller.mode === "lasso" && hasImage && !controller.activeInstance && (
                <p>캔버스에서 객체가 들어오도록 자유롭게 둘러 그리세요.</p>
              )}
              {controller.mode === "text" && hasImage && !controller.result && (
                <p>찾고 싶은 객체를 입력하면 일치하는 인스턴스를 모두 탐색합니다.</p>
              )}
              {controller.activeInstance && (
                <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                  <p className="font-semibold text-gray-700 dark:text-gray-200">
                    선택 객체 confidence{" "}
                    {(controller.activeInstance.score * 100).toFixed(1)}%
                  </p>
                  <p className="mt-0.5">
                    {roiResult
                      ? `후보 ${roiResult.count}개 · seed ${roiResult.search?.seeds_tested ?? 0}개`
                      : textResult
                        ? `“${textResult.prompt}” · 후보 ${textResult.count}개`
                        : `포인트 ${controller.points.length}개`}
                    {" · "}
                    추론 {controller.result?.inference_seconds.toFixed(3)}초
                  </p>
                </div>
              )}
              {roiResult && roiResult.count === 0 && (
                <p className="text-amber-600 dark:text-amber-300">
                  이 영역에서는 안정적인 객체 후보를 찾지 못했습니다. 영역을 조금 넓혀보세요.
                </p>
              )}
              {controller.mode === "text" && controller.result && controller.result.count === 0 && (
                <p className="text-amber-600 dark:text-amber-300">
                  일치하는 객체를 찾지 못했습니다. 더 단순한 영어 명사로 다시 검색해보세요.
                </p>
              )}
            </div>
          </aside>
          </div>
        )}

        <ObjectRiskAssessmentRunPanel
          state={run.state}
          focusTarget={run.focusTarget}
          artifact={run.artifact}
          sessionId={run.sessionId}
          stages={run.stages}
          progressPercent={run.progressPercent}
          error={run.error}
          localFocusUrl={controller.focusDraft?.previewUrl}
          onCancel={() => void run.cancel()}
        />
      </div>
    </section>
  );
}
